import type { ReactNode } from 'react';
import { useCallback } from 'react';

import { useHomeVersao } from './home-intl-context';

/**
 * Rótulo de um CTA de teste grátis da Home. Nas versões "Assine" da Home do par (`versao.cta === 'assine'`,
 * src/utils/home-intl/versoes.ts) vira o "Assine agora" do idioma do par (o destino troca junto: homeIntlTrialHref e
 * useTrialCta levam ao "Planos e preços"). Fora delas — a Home do par (v1), as outras Homes e qualquer outra página — devolve
 * o filho como veio: nenhum elemento a mais, o HTML fica idêntico.
 */
export function TrialCtaText({ children }: { children: ReactNode }) {
  const versao = useHomeVersao();
  // Fragmento de propósito: o componente devolve texto ou o filho como veio, sem elemento a mais no HTML.
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{versao?.cta === 'assine' ? versao.textos.assine : children}</>;
}

/** O mesmo, para quem precisa da string (aria-label do selo do hero). */
export function useTrialCtaLabel(label: string): string {
  const versao = useHomeVersao();
  return versao?.cta === 'assine' ? versao.textos.assine : label;
}

/**
 * Corta textos da Home com o trecho que a versão "Assine" tira (src/content/home-intl/ui/versoes/<grupo>.json, `cortes`),
 * pela chave `<arquivo>:<chave>` (ex.: `header-footer:footer.text`). Fora das versões Assine, sem corte para a chave ou
 * com o trecho ausente do texto, devolve o texto como veio. Em função, para quem corta vários textos com a mesma chave (as
 * perguntas e respostas do FAQ: uma lista só de alternativas, e cada texto perde a primeira que contém).
 */
export function useVersaoCortes(): (chave: string, texto: string) => string {
  const versao = useHomeVersao();
  const cortes = versao?.cta === 'assine' ? versao.textos.cortes : undefined;
  return useCallback(
    (chave: string, texto: string) => {
      const corte = cortes?.[chave];
      if (!corte) return texto;
      // uma lista: vale a primeira alternativa presente no texto — um trecho que sai, ou a troca mínima de → para
      const alternativa = (Array.isArray(corte) ? corte : [corte]).find((item) => texto.includes(typeof item === 'string' ? item : item.de));
      if (alternativa === undefined) return texto;
      // função como substituto: o texto novo entra literal (sem os padrões especiais $&, $1… do String.replace)
      return texto.replace(typeof alternativa === 'string' ? alternativa : alternativa.de, () => (typeof alternativa === 'string' ? '' : alternativa.para));
    },
    [cortes]
  );
}

/** Um texto da Home sem o trecho que a versão "Assine" corta (useVersaoCortes). */
export function useVersaoCorte(chave: string, texto: string): string {
  return useVersaoCortes()(chave, texto);
}

/** A página é uma versão "Assine" (os textos que falam do teste grátis somem). */
export function useIsAssineVersao(): boolean {
  return useHomeVersao()?.cta === 'assine';
}
